"use client";

import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import {
  BarChart3,
  FileText,
  Globe,
  Link2,
  Plug,
  ScanSearch,
  Store,
  type LucideIcon,
} from "lucide-react";

import { cn } from "../lib/cn";
import { nx, NX_RING_CLIP } from "../lib/onyx-material";
import { AnimatedExperienceTypeIcon } from "./AnimatedExperienceTypeIcon";
import { getExperienceTypeMeta, type ExperienceTypeTone } from "./ExperienceTypeBadge";
import { StatusChip, resolveStatus, statusToken } from "./StatusExpression";
import { Skeleton } from "../primitives/Skeleton";

/**
 * ArtifactTile — one card for every citizen of the Brain.
 *
 * The Brain (the multi-experience / multi-file session sidebar, unified#103)
 * renders everything a chat session produced — experiences, files, AI analyses,
 * references, connectors — as one grouped bento list. The adoption principle is
 * "the Brain is artifact-kind agnostic": every citizen is the **same tile** with
 * a different `kind`, so adding a kind is "give it an icon and a status, it
 * inherits tile / glow / drill-in for free". This is that single visual
 * primitive.
 *
 * Anatomy (one tile, every kind): a header row (icon tile + status indicator),
 * a 2-line title, and a mono meta/provenance line. The only place per-type
 * **color** appears is the 28x28 icon tile — title, meta, keyline, glow, and
 * status chrome stay monochrome slate/ink/accent (identity-only color, Handoff
 * L68). Supporting kinds (file/chart/analysis/link/connector/fetched/studio) are
 * fully neutral.
 *
 * - **Material** is the Onyx hex+notch (#158): the tile rides the `nx-s` card
 *   silhouette (rounded square + one top-right hex notch) with clip-safe
 *   `elev-card` elevation (a `box-shadow` would be sliced by the clip). The
 *   rest / focus / active outline is the continuous `NxKeyline` SVG stroke
 *   overlay — NOT a `border` (sliced at the notch) and NOT an inset shadow
 *   (clipped at the notch). Status dots are the flat-top `hex`, consistent with
 *   the rest of the Onyx language.
 * - **Status** is expressed in *form + color* via the canonical
 *   `StatusChip` / `statusToken` vocabulary (aisquare-ui#125): a `New` badge
 *   when `fresh`, else a settled dot (ready/published), a draft chip, or an
 *   in-progress chip (generating, with a shimmer background). A `failed`
 *   artifact NO LONGER paints a red destructive status dot — its attention is
 *   carried by the left accent keyline (see below, sb56xx).
 * - **Attention keyline** is a ~3px vertical accent bar on the tile's LEFT edge,
 *   shown for the `failed` status or an app-driven `attention` prop. It uses the
 *   accent token (never a raw red) and sits on the edge opposite the top-right
 *   notch, so it never collides with the clip. This replaces the retired red
 *   status dot, matching the app-side left accent line (sb56xx).
 * - **Cursor glow** is the `BorderGlow track="cursor"` masked-radial ring that
 *   follows the pointer in the accent token (not the per-type hue). The glow
 *   wrapper carries the SAME `nx-s` clip so the ring registers on the notch.
 * - **Entrance** is the transform-only `card-in` keyframe (translateY, never
 *   opacity-from-0, so an html-to-image export never captures a blank card).
 *   Stagger via a per-cell `style={{ animationDelay }}`.
 * - **`asChild`** lets the tile render as a `<button>` (default) or `<a>` so the
 *   consumer can attach the trace-back target (the originating `message_id`).
 *
 * Token-driven, theme-aware (light + `.dark`), reduced-motion safe (the glow is
 * not tracked, the icon is static, the entrance paints its final frame).
 *
 * Presentational only: it takes normalized props, not the raw #103 `/items/`
 * item; the `/items/` -> tile-props adapter lives in unified.
 */

export type ArtifactKind =
  | "experience"
  | "file"
  | "chart"
  | "analysis"
  | "link"
  | "connector"
  | "fetched"
  | "studio";

/**
 * Icon-tile tint, keyed by the library's `getExperienceTypeMeta` tone (the one
 * per-type palette source). Color lives ONLY here. Slate/neutral tones and all
 * supporting kinds resolve to a hue-free `muted` fill.
 */
const TONE_TILE: Record<ExperienceTypeTone, string> = {
  emerald: "bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-300",
  sky: "bg-sky-500/10 text-sky-600 dark:bg-sky-500/20 dark:text-sky-300",
  cyan: "bg-cyan-500/10 text-cyan-600 dark:bg-cyan-500/20 dark:text-cyan-300",
  violet: "bg-violet-500/10 text-violet-600 dark:bg-violet-500/20 dark:text-violet-300",
  amber: "bg-amber-500/10 text-amber-600 dark:bg-amber-500/20 dark:text-amber-300",
  rose: "bg-rose-500/10 text-rose-600 dark:bg-rose-500/20 dark:text-rose-300",
  slate: "bg-muted text-muted-foreground",
  neutral: "bg-muted text-muted-foreground",
};

/** Monochrome lucide glyph for each non-experience kind. */
const KIND_ICON: Record<Exclude<ArtifactKind, "experience">, LucideIcon> = {
  file: FileText,
  chart: BarChart3,
  analysis: ScanSearch,
  link: Link2,
  connector: Plug,
  fetched: Globe,
  // A published studio surface / storefront collection; storefront glyph, kept
  // hue-free like the other supporting kinds (the icon tile resolves to `muted`).
  studio: Store,
};

/** The header-right status indicator, by the #124 precedence. */
function StatusIndicator({ status, fresh }: { status: string; fresh: boolean }) {
  if (fresh) {
    return (
      <span className="shrink-0 rounded-[5px] bg-accent/10 px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase leading-none tracking-wide text-accent">
        New
      </span>
    );
  }
  const info = statusToken(status);
  // generating + draft carry a chip (label + motion); a settled state is a bare
  // token-colored dot with an accessible label. `failed` NO LONGER paints a red
  // destructive dot (sb56xx) — its attention is carried by the left accent
  // keyline on the tile; we keep the accessible `role="status"` label here so
  // the failure is still announced to assistive tech.
  if (info.status === "generating" || info.status === "draft") {
    return <StatusChip status={status} size="sm" />;
  }
  return (
    <span role="status" aria-label={info.label} className="inline-flex shrink-0 items-center">
      {info.status === "failed" ? null : (
        <span
          aria-hidden
          // round settled status dot (the flat-top hexagon dot was retired)
          className="inline-block h-1.5 w-1.5 rounded-full bg-success"
        />
      )}
    </span>
  );
}

export interface ArtifactTileProps extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  /** Which Brain citizen this is. Only the icon (and whether color is allowed) changes per kind. */
  kind: ArtifactKind;
  /** For `kind="experience"`: the experience type (quest/note/video/podcast/expert/...) driving icon + identity color. */
  experienceType?: string;
  /**
   * Overrides the kind-derived main glyph rendered in the 28x28 icon tile. The
   * app passes a provider mark for connectors (e.g. a Slack / Gmail logo) so
   * `kind="connector"` need not fall back to the generic plug. When omitted (or
   * nullish), the icon is derived from `kind` (and `experienceType` for
   * experiences) exactly as before. Pass a pre-sized glyph (the kind-derived
   * icons are `h-4 w-4`); the icon-tile tint is unchanged.
   */
  icon?: React.ReactNode;
  /** Lifecycle status (domain word or canonical state); resolved via `STATUS_ALIAS`. Default `draft`. */
  status?: string;
  /** Primary title (2-line clamp). */
  title: string;
  /** Provenance / dedupe line, e.g. `Quiz · 12 questions`, `PDF · 12 pages`, `used in 3 turns`. */
  meta?: string;
  /** Just created/uploaded this turn -> a `New` badge (highest-precedence indicator). */
  fresh?: boolean;
  /** This tile is the focused artifact in the router -> one accent ring. */
  active?: boolean;
  /**
   * Drives the left accent keyline (sb56xx) — a ~3px vertical accent bar on the
   * tile's left edge signalling this artifact needs attention. `"error"` /
   * `"review"` (or `true`) show it; `false` explicitly suppresses it even for a
   * `failed` status. When omitted, a `failed` status shows the keyline
   * automatically (the replacement for the retired red status dot). The visual
   * is one accent line for every reason; the reason is surfaced on
   * `data-attention` for consumer styling / analytics.
   */
  attention?: "error" | "review" | boolean;
  /** Render as the consumer's own element (`<a>`/`<button>`) for the trace-back target. */
  asChild?: boolean;
}

/**
 * The ref points at the **interactive element** (the `<button>`/`<a>`/slotted
 * host), not the outer glow wrapper, so a consumer can focus or scroll the
 * trace-back target.
 */
export const ArtifactTile = React.forwardRef<HTMLButtonElement, ArtifactTileProps>(
  (
    {
      kind,
      experienceType,
      icon,
      status = "draft",
      title,
      meta,
      fresh = false,
      active = false,
      attention,
      asChild = false,
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    const [hovered, setHovered] = React.useState(false);
    const Comp = asChild ? Slot : "button";

    const typeMeta =
      kind === "experience" ? getExperienceTypeMeta(experienceType ?? "experience") : null;
    const tileTint = typeMeta ? TONE_TILE[typeMeta.tone] : "bg-muted text-muted-foreground";

    // A consumer-supplied `icon` overrides the kind-derived glyph (sb57w3, e.g. a
    // connector's provider mark). When omitted, derive it from `kind` /
    // `experienceType` exactly as before.
    const iconNode =
      icon ??
      (kind === "experience" ? (
        <AnimatedExperienceTypeIcon
          type={experienceType ?? ""}
          active={hovered}
          size="sm"
          fallback={React.createElement(typeMeta!.Icon, {
            className: "h-4 w-4",
            "aria-hidden": true,
          })}
        />
      ) : (
        // `kind` is narrowed to a non-experience kind in this branch.
        React.createElement(KIND_ICON[kind], { className: "h-4 w-4", "aria-hidden": true })
      ));

    // sb56xx: a `failed` status (or an app-driven `attention`) shows the left
    // accent keyline in place of the retired red status dot. `attention={false}`
    // explicitly opts out even for a failed tile. The visual is one accent line;
    // the resolved reason ("error" | "review") rides `data-attention`.
    const attentionKind =
      attention === false
        ? null
        : attention === "error" || attention === "review"
          ? attention
          : attention === true || resolveStatus(status) === "failed"
            ? "error"
            : null;

    const toggleHover = (next: boolean) => () => setHovered(next);

    return (
      // Entrance wrapper: transform-only `card-in` (kept off the tile so it does
      // not fight the tile's hover-lift transform). The border is the `nx-rim`
      // RING overlay below (clean at every corner + the notch); the old cursor
      // BorderGlow clipped its ring on the corners, so it is retired here.
      <div className="block motion-safe:animate-card-in">
        <Comp
          ref={ref}
          data-kind={kind}
          data-status={resolveStatus(status)}
          data-active={active || undefined}
          data-attention={attentionKind ?? undefined}
          onMouseEnter={toggleHover(true)}
          onMouseLeave={toggleHover(false)}
          onFocus={toggleHover(true)}
          onBlur={toggleHover(false)}
          className={cn(
            // Onyx hex+notch material: the `nx-s` clip + clip-safe `elev-card`
            // (a box-shadow would be sliced by the clip). The outline is the
            // continuous `nx-rim` ring overlay below — NOT a real border (sliced
            // at the notch) and NOT an inset shadow (clipped at the notch).
            nx.s,
            "relative block w-full cursor-pointer bg-card p-3 text-left elev-card",
            "transition-transform duration-200 ease-smooth-out",
            // Focus is carried by the `--ring` keyline (the nx-rim below); we
            // suppress the browser outline (a ring would paint a rectangle past
            // the notch).
            "hover:-translate-y-px focus-visible:outline-none",
            // Active selection: a soft accent GLOW (a drop-shadow survives the
            // clip) layered onto the `--ring` keyline (the nx-rim below).
            active && "nx-keyline-active",
            className,
          )}
          {...(asChild ? {} : { type: "button" as const })}
          {...rest}
        >
          {/* asChild: merge our content onto the consumer's element (e.g. an
              <a href> trace-back target) via Slottable; otherwise it is just our
              own <button>'s content. */}
          {asChild ? <Slottable>{children}</Slottable> : null}
          <span className="flex items-center justify-between">
            <span
              className={cn(
                "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg",
                tileTint,
              )}
            >
              {iconNode}
            </span>
            <StatusIndicator status={status} fresh={fresh} />
          </span>
          <span className="mt-2.5 line-clamp-2 block text-[12.5px] font-semibold leading-snug text-foreground">
            {title}
          </span>
          {meta ? (
            <span className="mt-1 block font-mono text-[9.5px] leading-tight text-muted-foreground">
              {meta}
            </span>
          ) : null}
          {/* Left accent keyline (sb56xx): a ~3px vertical accent bar on the
              left edge for the attention / error state, replacing the retired
              red status dot. The `nx-s` clip rounds its left corners to match
              the tile; the top-right notch is on the opposite edge, so the
              keyline never collides with the clip. Decorative (`aria-hidden`) —
              the accessible status label lives in the header indicator. Uses the
              accent token, never a raw red. */}
          {attentionKind ? (
            <span
              aria-hidden
              data-attention-keyline=""
              className="absolute inset-y-0 left-0 z-[2] w-[3px] bg-accent pointer-events-none"
            />
          ) : null}
          {/* Continuous border: the `nx-rim` shape() RING (pure %/calc), so it
              traces every rounded corner AND the notch with no measurement and
              no clipping. Rest = neutral hairline; hover / active = the same
              even `--ring` focus keyline the text inputs use on focus (NOT the
              heavier, more-saturated `--accent`, which read as an off-color,
              uneven border here). One token, one intensity, every state. */}
          <span
            aria-hidden
            className="nx-rim absolute inset-0 z-[1] pointer-events-none transition-[background] duration-200"
            style={
              {
                "--nx-rim":
                  active || hovered ? "hsl(var(--ring))" : "var(--cardBd, hsl(var(--border)))",
                clipPath: NX_RING_CLIP.s,
              } as React.CSSProperties
            }
          />
        </Comp>
      </div>
    );
  },
);
ArtifactTile.displayName = "ArtifactTile";

/**
 * ArtifactTileSkeleton — the loading placeholder tile (shimmer on a card base),
 * matching the tile's footprint so a loading grid does not reflow when real
 * tiles arrive.
 */
export const ArtifactTileSkeleton = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...rest }, ref) => (
  // Same `nx-s` notched footprint + clip-safe `elev-card` as the real tile, so a
  // loading grid does not reflow when tiles arrive. The resting keyline traces
  // the silhouette so the skeleton reads as the same Onyx material.
  <div ref={ref} className={cn(nx.s, "relative bg-card p-3 elev-card", className)} {...rest}>
    <div className="flex items-center justify-between">
      <Skeleton className="h-7 w-7 rounded-lg" animation="shimmer" />
      <Skeleton className="h-2 w-10 rounded-full" animation="shimmer" />
    </div>
    <Skeleton className="mt-2.5 h-3.5 w-3/4 rounded" animation="shimmer" />
    <Skeleton className="mt-1.5 h-2.5 w-1/2 rounded" animation="shimmer" />
    <span
      aria-hidden
      className="nx-rim absolute inset-0 pointer-events-none"
      style={
        {
          "--nx-rim": "var(--cardBd, hsl(var(--border)))",
          clipPath: NX_RING_CLIP.s,
        } as React.CSSProperties
      }
    />
  </div>
));
ArtifactTileSkeleton.displayName = "ArtifactTileSkeleton";

/** Convenience static so consumers can write `<ArtifactTile.Skeleton />`. */
(ArtifactTile as typeof ArtifactTile & { Skeleton: typeof ArtifactTileSkeleton }).Skeleton =
  ArtifactTileSkeleton;
